import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Share2, FileSignature, HeartPulse, Download, UserX, Trash2, LogOut, Star, Ribbon } from "lucide-react";

const LANG_LABELS = { de: "Deutsch", en: "English", es: "Español", fr: "Français" };
const PREFS = [["technique", "by_technique"], ["emotion", "by_emotion"], ["base_situation", "by_base_situation"]];

export default function Settings() {
  const { t, lang, setLang, langs } = useI18n();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  usePageMeta(t("settings"));
  const [pref, setPref] = useState(user?.profile?.event_display_pref || "technique");
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    api.get("/user-blocks").then((r) => setBlocks(Array.isArray(r.data) ? r.data : (r.data?.blocks || []))).catch(() => setBlocks([]));
  }, []);

  const savePref = async (k) => {
    setPref(k);
    try { await api.patch("/auth/profile", { event_display_pref: k }); await refreshUser(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onExport = async () => {
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `my-within-export-${Date.now()}.json`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success(t("export_started"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const unblock = async (id) => {
    try { await api.delete(`/user-blocks/${id}`); setBlocks((b) => b.filter((x) => (x.blocked_id || x.id) !== id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onDelete = async () => {
    if (!window.confirm(t("delete_account_confirm_body"))) return;
    try {
      const { data } = await api.post("/me/delete-request");
      const code = data.confirm_code;
      const entry = window.prompt(t("delete_code_prompt", { code }));
      if (!entry || entry.trim().toUpperCase() !== code) { toast.error(t("delete_code_mismatch")); return; }
      await api.delete("/me", { data: { confirm_code: code, reason: "user-requested" } });
      await logout();
      navigate("/");
    } catch (e) { toast.error(apiError(e)); }
  };

  const Row = ({ icon: Icon, title, sub, onClick, to, danger, testId }) => (
    <button onClick={onClick || (() => navigate(to))} data-testid={testId}
      className={`flex w-full items-center gap-3 rounded-2xl border bg-card p-3.5 text-left transition-colors hover:bg-accent ${danger ? "border-destructive/40" : "border-border"}`}>
      <Icon className={`h-5 w-5 shrink-0 ${danger ? "text-destructive" : "text-primary"}`} />
      <span className="min-w-0 flex-1">
        <span className={`block text-sm font-semibold ${danger ? "text-destructive" : "text-foreground"}`}>{title}</span>
        {sub ? <span className="block text-xs text-muted-foreground">{sub}</span> : null}
      </span>
      {to ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : null}
    </button>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="settings-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="settings-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-2xl text-foreground">{t("settings")}</h1>
      </div>

      <section className="space-y-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("language")}</h2>
        <div className="flex flex-wrap gap-2">
          {langs.map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${lang === l ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>{LANG_LABELS[l] || l}</button>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("event_display_pref")}</h2>
        <div className="flex flex-wrap gap-2">
          {PREFS.map(([k, label]) => (
            <button key={k} onClick={() => savePref(k)} data-testid={`pref-${k}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${pref === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>{t(label)}</button>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <Row icon={Star} title={t("upgrade_plan")} to="/app/credits" testId="settings-upgrade" />
        {user?.role === "client" ? <Row icon={Ribbon} title={t("upgrade_to_coach")} to="/app/account/become-coach" testId="settings-become-coach" /> : null}
      </section>

      <section className="space-y-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("privacy_and_data")}</h2>
        <Row icon={Share2} title={t("data_sharing")} sub={t("data_sharing_sub")} to="/app/account/data-sharing" testId="settings-data-sharing" />
        <Row icon={FileSignature} title={t("consent_forms_title")} sub={t("consent_forms_sub")} to="/app/account/consent" testId="settings-consent" />
        <Row icon={HeartPulse} title={t("need_help_now")} sub={t("crisis_helplines")} to="/app/account/safety" danger testId="settings-safety" />
        <Row icon={Download} title={t("download_my_data")} sub={t("gdpr_art20")} onClick={onExport} testId="settings-export" />
      </section>

      <section className="space-y-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("blocked_users_title")}</h2>
        {blocks.length === 0 ? (
          <p className="px-1 text-xs italic text-muted-foreground" data-testid="blocked-empty">{t("blocked_users_empty")}</p>
        ) : blocks.map((b) => {
          const id = b.blocked_id || b.id;
          return (
            <div key={id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`blocked-${id}`}>
              <UserX className="h-4 w-4 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm text-foreground">{b.blocked_name || b.name || id}</span>
              <button onClick={() => unblock(id)} data-testid={`unblock-${id}`} className="rounded-full border border-border px-3 py-1 text-xs font-medium text-foreground hover:bg-accent">{t("unblock")}</button>
            </div>
          );
        })}
      </section>

      <section className="space-y-2">
        <Row icon={Trash2} title={t("delete_my_account")} sub={t("delete_my_account_sub")} onClick={onDelete} danger testId="settings-delete" />
      </section>

      <button onClick={logout} data-testid="settings-logout" className="mx-auto flex items-center gap-2 py-2 text-sm font-semibold text-destructive">
        <LogOut className="h-4 w-4" /> {t("sign_out")}
      </button>
      <p className="pb-4 text-center text-[11px] leading-relaxed text-muted-foreground">{t("disclaimer")}</p>
    </div>
  );
}
